import type { AssetResult } from '@cesdk/cesdk-js';
import { GQLDesignFragment } from '@theprintbar/graphql';
import { ImageSize, WebRoutes, getSizedImageSourceSet, invariant, pixelsToMM, sizedImage, withDomain } from '@theprintbar/shared';
import type { CreativeEditorInstance } from '../../../../CreativeEditor';
import { ExportLabel, setExportLabel } from '../../../exportMetadata';
import { getUploadsFromStorage, isLocalUpload, localAssetType, removeUploadFromStorage } from '../../../imageHandlers/upload';
import { getPageScaleFactor } from '../../../page';
import { BGRemoverConfig } from '../../setupCanvas';

export const designLocalAssetMeta = 'localAsset';
export const designAssetMeta = 'designId';

interface AddDesignsParams {
	editor: CreativeEditorInstance;
	assetURL: string;
	designHandlerUrl: string;
	webDomain: string;
}

let bgPreloadPromise: Promise<void> | null = null;

function scheduleBgRemoverPreloadOnce(assetURL: string) {
	if (bgPreloadPromise) return;

	bgPreloadPromise = new Promise<void>(resolve => {
		const run = async () => {
			try {
				const { preload } = await import('@imgly/background-removal');
				preload(BGRemoverConfig(assetURL).provider.configuration);
			} finally {
				resolve();
			}
		};

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				if ('requestIdleCallback' in window) {
					(window as any).requestIdleCallback(() => void run(), { timeout: 2000 });
				} else {
					setTimeout(() => void run(), 0);
				}
			});
		});
	});
}

export function addDesigns({ editor, assetURL, designHandlerUrl, webDomain }: AddDesignsParams) {
	if (!editor) return '';
	const designId = 'images.designs';
	const applyAsset = async (asset: AssetResult) => {
		const scene = editor.engine.scene.get();
		if (scene !== null) {
			const hasLoaded = editor.engine.block.hasMetadata(scene, 'bgRemoverModelLoaded');
			const expired =
				hasLoaded &&
				Date.now() - parseInt(editor.engine.block.getMetadata(scene, 'bgRemoverModelLoaded') || '0', 10) > 1000 * 60 * 60 * 24;
			if (!hasLoaded || expired) {
				//Start a non render blocking preload of the bg remover model
				editor.engine.block.setMetadata(scene, 'bgRemoverModelLoaded', Date.now().toString());
				scheduleBgRemoverPreloadOnce(assetURL);
			}
		}

		const pageScale = getPageScaleFactor(editor.engine);
		const result = await editor.engine.asset.defaultApplyAsset(asset as AssetResult);
		if (result && !!asset.meta) {
			let newWidth = pixelsToMM(asset.meta.width!, 300) * pageScale;
			let newHeight = pixelsToMM(asset.meta.height!, 300) * pageScale;

			//Dont exceed the max canvas size
			const page = editor.engine.scene.getCurrentPage();
			invariant(page !== null, 'No page found');
			const pageWidth = editor.engine.block.getWidth(page);
			const pageHeight = editor.engine.block.getHeight(page);
			if (newWidth > pageWidth || newHeight > pageHeight) {
				const widthRatio = pageWidth / newWidth;
				const heightRatio = pageHeight / newHeight;
				const ratio = Math.min(widthRatio, heightRatio) * 0.9;
				newWidth = newWidth * ratio;
				newHeight = newHeight * ratio;
				//Put the image in the center of the page
				const left = (pageWidth - newWidth) / 2;
				const top = (pageHeight - newHeight) / 2;
				editor.engine.block.setPosition(result, left, top);
				editor.engine.block.setRotation(result, 0);
			}
			editor.engine.block.setWidth(result, Math.floor(newWidth));
			editor.engine.block.setHeight(result, Math.floor(newHeight));

			//Everything in this library is the customer's own artwork, fresh or reused
			setExportLabel(editor, result, ExportLabel.Upload);

			editor.engine.block.setMetadata(result, designAssetMeta, asset.id);
			if (asset.meta?.fileSize) {
				editor.engine.block.setMetadata(result, 'fileSize', asset.meta.fileSize as string);
			}
			editor.engine.block.setMetadata(result, 'fillType', asset.meta?.fillType || '//ly.img.ubq/fill/image');
			// editor.engine.block.setEnum(result, 'contentFill/mode', 'Contain');
			if (asset.meta?.type === localAssetType) {
				editor.engine.block.setMetadata(result, designLocalAssetMeta, asset.id);
			}
		}

		return result;
	};

	editor?.engine.asset.addSource({
		id: designId,
		getSupportedMimeTypes: () => ['image/png', 'image/jpeg', 'image/webp', 'image/bmp', 'image/svg+xml'],
		addAsset: async asset => {
			const assetLibraryOpen = editor.ui.isPanelOpen('//ly.img.panel/assetLibrary');
			if (!assetLibraryOpen) return;
			await applyAsset({
				...asset,
				label: asset.label?.en || '',
				tags: [],
			});
			editor.ui.closePanel('//ly.img.panel/assetLibrary');
		},
		applyAsset,
		applyAssetToBlock: async (asset, block) => {
			await editor.engine.asset.defaultApplyAssetToBlock(asset, block);
			setExportLabel(editor, block, ExportLabel.Upload);
		},
		removeAsset: async asset => {
			if (!(await isLocalUpload(asset))) {
				await fetch(designHandlerUrl, {
					method: 'DELETE',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						id: asset,
					}),
				});
			}
			await removeUploadFromStorage(asset.split('|')[1] || asset);
			editor.engine.asset.assetSourceContentsChanged(designId);
			return;
		},
		findAssets: async queryData => {
			//Delay for half a second
			await new Promise(resolve => setTimeout(resolve, 500));
			let uploads = await getUploadsFromStorage();

			let designs: GQLDesignFragment[] = [];
			try {
				designs = await (await fetch(designHandlerUrl)).json();
			} catch (e) {
				console.error('Failed to fetch designs', e);
			}

			uploads = uploads.filter(upload => !designs.some(design => `${design.image.id}.${design.image.extension}` === upload.id));

			//Remove local uploads if a new design replaced it
			let assets: AssetResult[] = [
				...uploads.map(upload => ({
					...upload,
					tags: undefined,
					label: upload.label?.en,
				})),
				...designs.reduce((acc, design) => {
					if (!acc.find(d => d.id.endsWith(`${design.image.id}.${design.image.extension}`))) {
						// Point at the design endpoint so lazy-cropped designs (Design.cropS*)
						// hand the editor the cropped bytes, not the raw source. For uncropped
						// designs the endpoint 302s to viewUpload — same asset as before.
						const hasCrop = design.cropSx != null && design.cropSy != null && design.cropSw != null && design.cropSh != null;
						const designUrl = WebRoutes.API.images.designImage({ designId: design.id, webDomain });
						const width = hasCrop ? design.cropSw! : design.image.width;
						const height = hasCrop ? design.cropSh! : design.image.height;
						acc.push({
							id: `${design.id}|${design.image.id}.${design.image.extension}`,
							label: design.name,
							payload: {
								sourceSet: getSizedImageSourceSet({
									url: designUrl,
									originalHeight: height,
									originalWidth: width,
								}),
							},
							meta: {
								designId: design.id,
								//Get the ms timestamp from the updatedAt field
								insertedAt: new Date(design.updatedAt).getTime(),
								uri: designUrl,
								thumbUri: sizedImage(designUrl, ImageSize.Medium),
								mimeType: 'image/png',
								width,
								height,
								fileSize: design.image.filesize,
							},
						});
					}
					return acc;
				}, [] as AssetResult[]),
			];

			// Apply sorting
			assets = assets.sort((a, b) => {
				return (b.meta?.insertedAt as number) || 0 - (a.meta?.insertedAt as number) || 0;
			});

			// Apply pagination
			const { page, perPage } = queryData;
			const startIndex = page * perPage;
			const endIndex = startIndex + perPage;
			const paginatedAssets = assets.slice(startIndex, endIndex);

			// Determine if there's a next page
			const nextPage = endIndex < assets.length ? page + 1 : undefined;
			return {
				assets: paginatedAssets,
				currentPage: page,
				nextPage,
				total: assets.length,
			};
		},
	});

	return designId;
}
